import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-react";
import { loadRazorpayScript } from "@/lib/razorpay";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "INR", symbol: "₹" },
];

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialPlan = searchParams.get("plan") || "Custom Amount";
  const urlAmount = searchParams.get("amount") || "";
  const amountLocked = !!urlAmount && parseFloat(urlAmount) > 0;

  const [plan] = useState(initialPlan);
  const [amount, setAmount] = useState(urlAmount);
  const [currency, setCurrency] = useState("USD");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [postConsent, setPostConsent] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [method, setMethod] = useState("card");
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, client_id: "" });
  const [quantity, setQuantity] = useState(1);

  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "$";

  const perUnit = parseFloat(amount) || 0;
  const numericAmount = perUnit;
  const totalAmount = Math.round(perUnit * quantity * 100) / 100;
  const orderPlan = quantity > 1 ? `${plan} — ${quantity} units` : plan;

  useEffect(() => {
    loadRazorpayScript();
    window.scrollTo(0, 0);
    axios.get(`${API}/payments/paypal/config`).then(({ data }) => setPaypalCfg(data)).catch(() => {});
  }, []);

  const handlePay = async () => {
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms & Refund Policy to continue");
      return;
    }

    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load Razorpay. Check your connection.");
        setLoading(false);
        return;
      }

      // Force US (+1) country code on the Razorpay phone field
      let normalizedContact = (contact || "").trim();
      const digits = normalizedContact.replace(/[^0-9]/g, "");
      if (normalizedContact.startsWith("+")) {
        // keep as entered
      } else if (digits) {
        normalizedContact = "+1" + digits;
      } else {
        normalizedContact = "+1";
      }

      const { data } = await axios.post(`${API}/payments/create-order`, {
        amount: totalAmount,
        currency,
        plan: orderPlan,
        name,
        email,
        contact: normalizedContact,
        consent: true,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Radio App Consultancy",
        description: `Radio App Consultancy — ${orderPlan}`,
        order_id: data.order_id,
        prefill: { name, email, contact: normalizedContact },
        theme: { color: "#3B82F6" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful!");
            setSuccess({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              amount: totalAmount,
              currency,
              plan: orderPlan,
            });
          } catch (err) {
            toast.error("Payment verification failed. Contact support.");
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!postConsent) {
      toast.error("Please tick the confirmation box first");
      return;
    }
    setConfirming(true);
    try {
      await axios.post(`${API}/payments/confirm`, { order_id: success.orderId });
      setConfirmed(true);
      toast.success("Payment confirmed. Thank you!");
    } catch (err) {
      toast.error("Could not save confirmation. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (success) {
    const money = `${CURRENCIES.find((c) => c.code === success.currency)?.symbol || "$"}${Number(success.amount).toFixed(2)}`;
    return (
      <div className="min-h-screen bg-[#F5F7FC] flex items-center justify-center px-6 py-10" data-testid="payment-success-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="max-w-md w-full rounded-2xl bg-white border border-[#E2E8F0] shadow-xl p-8 text-center"
        >
          <span className="mx-auto h-14 w-14 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mb-5">
            <CheckCircle2 className="h-8 w-8 text-[#3B82F6]" />
          </span>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">Payment successful</h1>
          <p className="text-[#475569] mt-2 text-sm">
            Thank you! Your one-time payment to Radio App Consultancy was received.
          </p>

          {/* Receipt */}
          <div className="mt-5 rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] p-4 text-xs text-left space-y-2" data-testid="receipt-box">
            <div className="flex justify-between"><span className="text-[#475569]">Merchant</span><span className="text-[#0F172A] font-medium">Radio App Consultancy</span></div>
            <div className="flex justify-between"><span className="text-[#475569]">Plan / Item</span><span className="text-[#0F172A]">{success.plan}</span></div>
            <div className="flex justify-between"><span className="text-[#475569]">Amount paid</span><span className="text-[#0F172A] font-semibold">{money} {success.currency}</span></div>
            <div className="flex justify-between font-mono"><span className="text-[#475569]">Payment ID</span><span className="text-[#0F172A]">{success.paymentId}</span></div>
            <div className="flex justify-between"><span className="text-[#475569]">Status</span><span className="text-[#3B82F6] font-semibold">PAID</span></div>
          </div>

          {/* Post-payment confirmation / consent */}
          {!confirmed ? (
            <div className="mt-6 rounded-lg border border-[#3B82F6]/40 bg-[#3B82F6]/5 p-4 text-left" data-testid="confirm-box">
              <p className="text-sm font-semibold text-[#0F172A]">One last step — confirm your payment</p>
              <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={postConsent}
                  onChange={(e) => setPostConsent(e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-[#3B82F6] cursor-pointer"
                  data-testid="post-consent-checkbox"
                />
                <span className="text-xs text-[#475569] leading-relaxed">
                  Yes, I confirm that I authorized this payment of <span className="font-medium text-[#0F172A]">{money}</span> to Radio App Consultancy for the selected service, and I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline">Terms</a> &amp; <a href="/refund" target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline">Refund Policy</a>.
                </span>
              </label>
              <button
                onClick={handleConfirm}
                disabled={confirming || !postConsent}
                className="btn-sweep mt-4 w-full bg-[#3B82F6] text-[#0F172A] font-semibold py-3 rounded-full hover:glow-primary transition-shadow duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="confirm-payment-btn"
              >
                {confirming ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>) : (<><CheckCircle2 className="h-4 w-4" /> Yes, I confirm this payment</>)}
              </button>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 flex items-center gap-2.5 text-left" data-testid="confirmed-box">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <span className="text-sm text-green-700 font-medium">Payment confirmed. A copy of this receipt is saved for your records.</span>
            </div>
          )}

          <button
            onClick={() => navigate("/")}
            className="mt-5 w-full text-sm text-[#475569] hover:text-[#0F172A] transition-colors"
            data-testid="success-home-btn"
          >
            Back to home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#0F172A]" data-testid="checkout-page">
      <div className="max-w-xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A] transition-colors" data-testid="checkout-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="mt-6 space-y-6">
          {/* Summary bar */}
          <div className="rounded-2xl bg-[#26395F] text-white p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Logo />
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] bg-white/10 text-white/80 px-2.5 py-1 rounded-full">Merchant: Radio App Consultancy</span>
            </div>

            <div className="mt-5 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">Order summary</p>
                <h2 className="font-display text-lg font-bold mt-0.5">{orderPlan}</h2>
                <p className="text-xs text-white/60 mt-1">One-time payment · No subscription</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] uppercase tracking-wider text-white/50">Total due</p>
                <p className="font-display text-2xl font-bold text-[#3B82F6] leading-none mt-1">{symbol}{totalAmount.toFixed(2)}</p>
                <p className="text-[10px] text-white/50 mt-0.5">USD</p>
              </div>
            </div>

            <div className="mt-5 border-t border-white/10 pt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-white/50">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#3B82F6]" /> Secured by Razorpay</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#3B82F6]" /> Statement shows <span className="text-white/80 font-medium">Radio App Consultancy</span></span>
            </div>
          </div>

          {/* Form */}
          <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-sm p-6 sm:p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">Complete your payment</h1>
            <p className="text-sm text-[#475569] mt-1">One-time payment to Radio App Consultancy — enter your details to continue.</p>

            {/* Payment method toggle (top) */}
            {paypalCfg.enabled && (
              <div className="mt-6">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#475569]">Payment method</label>
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="method-toggle">
                  <button
                    type="button"
                    onClick={() => setMethod("card")}
                    className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-colors ${method === "card" ? "border-[#3B82F6] bg-[#3B82F6]/5 text-[#0F172A]" : "border-[#E2E8F0] text-[#475569] hover:border-[#C7CCDB]"}`}
                    data-testid="method-card-btn"
                  >
                    Pay with Razorpay
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("paypal")}
                    className={`rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-colors ${method === "paypal" ? "border-[#3B82F6] bg-[#3B82F6]/5 text-[#0F172A]" : "border-[#E2E8F0] text-[#475569] hover:border-[#C7CCDB]"}`}
                    data-testid="method-paypal-btn"
                  >
                    Pay with Debit or Credit Card
                  </button>
                </div>
              </div>
            )}

            {/* Amount */}
            <div className="mt-8 max-w-xs">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#475569]">
                {quantity > 1 ? "Total amount" : "Amount per unit"} {amountLocked && <span className="text-[#3B82F6] normal-case tracking-normal">(fixed)</span>}
              </label>
              <div className={`mt-2 flex items-center gap-2 border-b-2 transition-colors pb-2 ${amountLocked ? "border-[#3B82F6]/40" : "border-[#E2E8F0] focus-within:border-[#3B82F6]"}`}>
                <span className="text-xs font-bold uppercase tracking-wide text-[#3B82F6]">USD</span>
                <span className="font-display text-2xl text-[#A6ACC2]">{symbol}</span>
                {amountLocked || quantity > 1 ? (
                  <span className="flex-1 font-display text-2xl text-[#0F172A]" data-testid="checkout-amount-display">{totalAmount.toFixed(2)}</span>
                ) : (
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 bg-transparent font-display text-2xl outline-none placeholder:text-[#C7CCDB] w-full text-[#0F172A]"
                    data-testid="checkout-amount-input"
                  />
                )}
              </div>
              {quantity > 1 && (
                <p className="text-xs text-[#475569] mt-2" data-testid="amount-perunit-note">{symbol}{perUnit.toFixed(2)} per unit × {quantity}</p>
              )}
              {quantity === 1 && amountLocked && <p className="text-xs text-[#475569] mt-2">This amount is for one unit. Need more than one? Add units below.</p>}
            </div>

            {/* Units multiplier */}
            <div className="mt-6 max-w-xs" data-testid="vehicle-qty-block">
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#475569]">Number of units</label>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="h-11 w-11 rounded-lg border-2 border-[#E2E8F0] text-xl font-bold text-[#0F172A] hover:border-[#3B82F6] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="qty-minus-btn"
                >
                  −
                </button>
                <span className="min-w-[3rem] text-center font-display text-2xl text-[#0F172A]" data-testid="qty-value">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(20, q + 1))}
                  className="h-11 w-11 rounded-lg border-2 border-[#E2E8F0] text-xl font-bold text-[#0F172A] hover:border-[#3B82F6] transition-colors"
                  data-testid="qty-plus-btn"
                >
                  +
                </button>
              </div>
              {quantity > 1 && (
                <p className="text-sm text-[#0F172A] mt-3" data-testid="qty-breakdown">
                  {symbol}{perUnit.toFixed(2)} × {quantity} = <span className="font-bold text-[#3B82F6]">{symbol}{totalAmount.toFixed(2)}</span>
                </p>
              )}
              <p className="text-xs text-[#94A3B8] mt-1.5">One-time charge covers one unit. Need more than one? Increase the count — price multiplies per unit.</p>
            </div>

            {/* Billing details */}
            <div className="mt-8 space-y-5">
              <div>
                <label className="text-sm text-[#475569]">Full name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors placeholder:text-[#A6ACC2] text-[#0F172A]"
                  data-testid="checkout-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#475569]">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors placeholder:text-[#A6ACC2] text-[#0F172A]"
                  data-testid="checkout-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#475569]">Phone (optional)</label>
                <input
                  type="tel"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors placeholder:text-[#A6ACC2] text-[#0F172A]"
                  data-testid="checkout-contact-input"
                />
              </div>
            </div>

            {/* Payment method toggle */}
            <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="checkout-consent-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#3B82F6] cursor-pointer"
                data-testid="checkout-consent-checkbox"
              />
              <span className="text-sm text-[#475569] leading-relaxed">
                I have read and agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#3B82F6] font-medium hover:underline">Terms of Service</a> and{" "}
                <a href="/refund" target="_blank" rel="noreferrer" className="text-[#3B82F6] font-medium hover:underline">Refund Policy</a>, and I authorize this one-time payment.
              </span>
            </label>

            {method === "card" ? (
              <button
                onClick={handlePay}
                disabled={loading || !agreed}
                className="btn-sweep mt-6 w-full bg-[#3B82F6] text-[#0F172A] font-semibold py-4 rounded-full hover:glow-primary transition-shadow duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="checkout-pay-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</>
                ) : (
                  <><Lock className="h-4 w-4" /> Pay {symbol}{totalAmount.toFixed(2)}</>
                )}
              </button>
            ) : (
              <div className="mt-6" data-testid="paypal-area">
                {!agreed || !numericAmount || !name.trim() || !email.trim() ? (
                  <div className="rounded-xl border border-dashed border-[#C7CCDB] p-4 text-center text-sm text-[#94A3B8]" data-testid="paypal-gate">
                    Enter amount, your name & email, and agree to the terms to pay by credit card.
                  </div>
                ) : (
                  <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "USD", intent: "capture", locale: "en_US", "enable-funding": "card", "disable-funding": "paylater,credit,venmo" }}>
                    <PayPalButtons
                      fundingSource={FUNDING.CARD}
                      style={{ layout: "vertical", shape: "rect", color: "black", label: "pay", height: 48 }}
                      createOrder={async () => {
                        const { data } = await axios.post(`${API}/payments/paypal/create-order`, {
                          amount: totalAmount, plan: orderPlan, name, email, contact, consent: true,
                        });
                        return data.order_id;
                      }}
                      onApprove={async (data) => {
                        try {
                          const { data: cap } = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.orderID });
                          toast.success("Payment successful!");
                          setSuccess({ paymentId: cap.payment_id, orderId: data.orderID, amount: totalAmount, currency: "USD", plan: orderPlan });
                        } catch (err) {
                          toast.error("Payment could not be completed.");
                        }
                      }}
                      onError={() => toast.error("Payment error. Please try again.")}
                    />
                    <p className="text-xs text-[#94A3B8] text-center mt-2">You'll enter your card details securely on the next step · USD</p>
                  </PayPalScriptProvider>
                )}
              </div>
            )}

            <div className="mt-5 flex flex-col items-center gap-2" data-testid="accepted-methods">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#94A3B8]">We accept</span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {["Cards", "Apple Pay", "Google Pay", "PayPal", "UPI"].map((m) => (
                  <span key={m} className="text-xs font-medium text-[#475569] bg-[#F5F7FC] border border-[#E2E8F0] px-3 py-1.5 rounded-full">
                    {m}
                  </span>
                ))}
              </div>
            </div>

            <p className="text-xs text-[#94A3B8] text-center mt-4">
              By continuing you agree to our terms. Payments are processed securely via Razorpay &amp; PayPal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
